import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DataTable, BaseRunners, columnTypes as colType, pitchType, dateTranslate, mapPlaylistFromVideoSearch } from 'yankees-react-utils';
import VideoPlayerButton from '../../../../components/VideoPlayerButton';
import VideoPlaylistButton from '../../../../components/VideoPlaylistButton';
import { store } from '../../../../store';

const ThrowingLog = ({ isPickoff, throwLogData }) => {
    const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    if (!throwLogData || throwLogData.length === 0) {
        return (<div>{translate('sections.reports.catcher.throwing.no-throws')}</div>);
    }
    const rows = throwLogData.map((t) => {
        const videoparams = { gameid: t.gameid, eventseq: t.eventseq, pitchseq: t.pitchseq };
        const videoplaylist = (<VideoPlayerButton {...videoparams} />);
        return { ...t, videoplaylist };
    });

    const playlist = mapPlaylistFromVideoSearch({ videos: rows, playervideos: [] }, user.Roles.map((r) => r.RoleName));

    const columns = isPickoff ? [
        { id: 'videoplaylist',
            title: (<VideoPlaylistButton
                icon={translate('common.all')}
                playlist={playlist}
            />),
            type: colType.NODE },
        { id: 'date', title: translate('common.date'), transform: (row) => dateTranslate(translate, row.gamedate), type: colType.TEXT },
        { id: 'opp', title: translate('sections.reports.catcher.throwing.abbr-opponent'), transform: (row) => row.battingteamabbr, type: colType.TEXT },
        { id: 'inn', title: translate('baseball.abbr-inning'), transform: (row) => row.inning },
        { id: 'outs', title: translate('baseball.outs_other'), transform: (row) => <span>{row.outsbefore}</span>, type: colType.NODE },
        { id: 'run', title: translate('sections.reports.catcher.throwing.run'), transform: (row) => (<BaseRunners baseState={row.bcbefore} emptyColor="#98CBFF" height={20} width={30} />), type: colType.NODE },
        { id: 'count', title: translate('baseball.count'), transform: (row) => `${row.balls}-${row.strikes}`, type: colType.TEXT },
        { id: 'runner', title: translate('baseball.runners_one'), transform: (row) => row.runnername, type: colType.TEXT },
        { id: 'pitcher', title: translate('baseball.pitcher_one'), transform: (row) => row.pitchername, type: colType.TEXT },
        { id: 'bats', title: translate('baseball.bats'), transform: (row) => (row.islhb ? 'L' : 'R'), type: colType.TEXT },
        { id: 'indirt', title: translate('sections.reports.catcher.throwing.in-dirt'), transform: (row) => (row.pitchindirt ? translate('common.yes') : translate('common.no')), type: colType.TEXT },
        { id: 'base', title: translate('sections.reports.catcher.throwing.base'), transform: (row) => row.base },
        { id: 'error', title: translate('sections.reports.catcher.throwing.throwing-error'), transform: (row) => (row.te ? translate('common.yes') : translate('common.no')), type: colType.TEXT },
    ] : [
        { id: 'videoplaylist', title: (<VideoPlaylistButton icon={translate('common.all')} playlist={playlist} />), type: colType.NODE },
        { id: 'date', title: translate('common.date'), transform: (row) => dateTranslate(translate, row.gamedate), type: colType.TEXT },
        { id: 'opp', title: translate('sections.reports.catcher.throwing.abbr-opponent'), transform: (row) => row.battingteamabbr, type: colType.TEXT },
        { id: 'inn', title: translate('baseball.abbr-inning'), transform: (row) => row.inning },
        { id: 'outs', title: translate('baseball.outs_other'), transform: (row) => <span>{row.outsbefore}</span>, type: colType.NODE },
        { id: 'run', title: translate('sections.reports.catcher.throwing.run'), transform: (row) => (<BaseRunners baseState={row.bcbefore} emptyColor="#98CBFF" height={20} width={30} />), type: colType.NODE },
        { id: 'count', title: translate('baseball.count'), transform: (row) => `${row.balls}-${row.strikes}`, type: colType.TEXT },
        { id: 'runner', title: translate('baseball.runners_one'), transform: (row) => row.runnername, type: colType.TEXT },
        { id: 'pitcher', title: translate('baseball.pitcher_one'), transform: (row) => row.pitchername, type: colType.TEXT },
        { id: 'bats', title: translate('baseball.bats'), transform: (row) => (row.islhb ? 'L' : 'R'), type: colType.TEXT },
        { id: 'pitch', title: translate('baseball.pitches_one'), transform: (row) => (row.result === 3 ? translate('sections.reports.catcher.throwing.pitchout') : pitchType(row.pitchtype, true, undefined, translate)), type: colType.TEXT },
        { id: 'indirt', title: translate('sections.reports.catcher.throwing.in-dirt'), transform: (row) => (row.pitchindirt ? translate('common.yes') : translate('common.no')), type: colType.TEXT },
        { id: 'play', title: translate('sections.reports.catcher.throwing.play'), transform: (row) => row.playtype, type: colType.TEXT },
        { id: 'base', title: translate('sections.reports.catcher.throwing.base'), transform: (row) => row.base },
        { id: 'delay', title: translate('sections.reports.catcher.throwing.delay-q'), transform: (row) => (row.delayedsteal ? translate('common.yes') : translate('common.no')), type: colType.TEXT },
        { id: 'throw', title: translate('sections.reports.catcher.throwing.throw-q'), transform: (row) => (row.isthrow ? translate('common.yes') : translate('common.no')), type: colType.TEXT },
        { id: 'knees', title: translate('sections.reports.catcher.throwing.knees-q'), transform: (row) => (row.fromknees ? translate('common.yes') : translate('common.no')), type: colType.TEXT },
        { id: 'rslt', title: translate('sections.reports.catcher.throwing.abbr-result'), transform: (row) => row.type, type: colType.TEXT },
        { id: 'exchange', title: translate('sections.reports.catcher.throwing.abbr-exchange'), transform: (row) => row.transfertime, precision: 2 },
        { id: 'throwtime', title: translate('sections.reports.catcher.throwing.throw'), transform: (row) => row.throwtime, precision: 2 },
        { id: 'poptime', title: translate('sections.reports.catcher.throwing.pop-time'), transform: (row) => row.catchertime, precision: 2 },
        { id: 'pitchertime', title: translate('sections.reports.catcher.throwing.pitcher-time'), transform: (row) => row.pitchertime, precision: 2 },
        { id: 'error', title: translate('sections.reports.catcher.throwing.throwing-error'), transform: (row) => (row.te ? translate('common.yes') : translate('common.no')), type: colType.TEXT },
    ];
    return (<DataTable columns={columns} rows={rows} />);
};

ThrowingLog.propTypes = {
    isPickoff: PropTypes.bool,
    throwLogData: PropTypes.arrayOf(PropTypes.shape({
        gamedate: PropTypes.string,
        battingteamabbr: PropTypes.string,
        inning: PropTypes.number,
        outsbefore: PropTypes.number,
        bcbefore: PropTypes.number,
        balls: PropTypes.number,
        strikes: PropTypes.number,
        runnername: PropTypes.string,
        pitchername: PropTypes.string,
        islhb: PropTypes.boolean,
        result: PropTypes.number,
        pitchtype: PropTypes.number,
        pitchindirt: PropTypes.boolean,
        playtype: PropTypes.string,
        base: PropTypes.number,
        delayedsteal: PropTypes.boolean,
        isthrow: PropTypes.boolean,
        fromknees: PropTypes.boolean,
        type: PropTypes.string,
        transfertime: PropTypes.number,
        throwtime: PropTypes.number,
        catchertime: PropTypes.number,
        pitchertime: PropTypes.number,
        runnertimerr: PropTypes.number,
        runnertimepr: PropTypes.number,
        fieldertime: PropTypes.number,
        fieldercatch: PropTypes.string,
        fieldertag: PropTypes.string,
        slide: PropTypes.number,
    })).isRequired,
};

ThrowingLog.defaultProps = {
    isPickoff: false,
};

export default ThrowingLog;
